import React, { useContext } from 'react';

import { useSelector, useDispatch, shallowEqual } from 'react-redux';

import { token } from '@atlaskit/tokens';

import { BlockerIcon } from 'src/components/icon';
import {
  REQUEST_CHANGES,
  UNDO_REQUEST_CHANGES,
} from 'src/redux/pull-request/actions';
import {
  getCurrentPullRequestRequestChangesSlice,
  getIsChangesRequested,
  getIRAnalyticsTrackingAttributes,
  getIsCurrentPullRequestAuthor,
} from 'src/redux/pull-request/selectors';
import { getCurrentPullRequestActivityChangesRequestedEvent } from 'src/redux/pull-request/selectors/activity-selectors';
import { publishUiEvent } from 'src/utils/analytics/publish';

import ReviewerActionButton from '../components/pull-request-reviewer-action-button';
import {
  DiffStateContext,
  SelectedDiffType,
} from '../components/tabs/diff/diff-state-context';
import { KeyboardShortcutTooltip } from '../components/tabs/shortcuts/keyboard-shortcut-tooltip';
import { KeyboardShortcutsContext } from '../components/tabs/shortcuts/pull-request-keyboard-shortcuts-context';

import messages from './request-changes-button.i18n';

type RequestChangesButtonProps = {
  tabIndex?: number;
  removeRightBorderRadius?: boolean;
};

export const RequestChangesButton = ({
  tabIndex,
  removeRightBorderRadius,
}: RequestChangesButtonProps) => {
  const dispatch = useDispatch();
  const {
    isRequestingChangesDisabled,
    isRequestingChangesError,
    isRequestingChangesPending,
  } = useSelector(getCurrentPullRequestRequestChangesSlice, shallowEqual);
  const isChangesRequested = useSelector(getIsChangesRequested, shallowEqual);
  const changesRequestedAction = useSelector(
    getCurrentPullRequestActivityChangesRequestedEvent,
    shallowEqual
  );
  const isPrAuthor = useSelector(getIsCurrentPullRequestAuthor);
  const { iterativeReviewSelectedDiff } = useContext(DiffStateContext);
  const irTracking = useSelector(getIRAnalyticsTrackingAttributes);
  irTracking.irSelectedDiffType =
    SelectedDiffType[iterativeReviewSelectedDiff.selectedDiffType];

  const { requestChangesRef } = useContext(KeyboardShortcutsContext);

  const requestChanges = () => {
    publishUiEvent({
      action: 'clicked',
      actionSubject: 'button',
      actionSubjectId: 'PRReviewerRequestChanges',
      source: 'pullRequestScreen',
      // IA tracking attributes for event: `button clicked PRReviewerRequestChanges`
      attributes: {
        irTracking,
      },
    });
    dispatch({ type: REQUEST_CHANGES.REQUEST });
  };

  if (requestChangesRef) {
    requestChangesRef.current = () => {
      if (!isChangesRequested && !isPrAuthor) {
        requestChanges();
      }
    };
  }

  const undoRequestChanges = () =>
    dispatch({ type: UNDO_REQUEST_CHANGES.REQUEST });
  const requestChangesMessages = {
    tooltipMessage: messages.requestChangesActionInfo,
    makeActionLabel: messages.requestChangesLabel,
    undoActionLabel: messages.undoRequestChangesLabel,
    makeActionMessage: isChangesRequested
      ? messages.requestChangesUndoAction
      : messages.requestChangesAction,
  };

  const ChangesButton = (
    <ReviewerActionButton
      messages={requestChangesMessages}
      mainIcon={
        <BlockerIcon primaryColor={token('color.icon.warning')} label="" />
      }
      tabIndex={tabIndex}
      isError={isRequestingChangesError}
      isLoading={isRequestingChangesPending}
      isDisabled={isRequestingChangesDisabled}
      isSelected={isChangesRequested}
      actionDate={
        changesRequestedAction ? new Date(changesRequestedAction.date) : ''
      }
      makeAction={requestChanges}
      undoAction={undoRequestChanges}
      removeRightBorderRadius={removeRightBorderRadius}
    />
  );

  // Show the keyboard shortcut tooltip only if the PR has no changes requested
  return !isChangesRequested ? (
    <KeyboardShortcutTooltip
      text={requestChangesMessages.makeActionMessage}
      shortcut="R"
      position="top"
    >
      {ChangesButton}
    </KeyboardShortcutTooltip>
  ) : (
    ChangesButton
  );
};

export default React.memo(RequestChangesButton);
